import { useSignIn, useSignUp, useClerk, useUser } from '@clerk/nextjs'
import { useMutation } from '@tanstack/react-query'
import { createAuthHandler, baseMutationConfig, handleMutationError } from '@/lib/sentry/client-errors'
import { useRouter } from 'next/navigation'

export const useClerkAuth = () => {
  const { signIn, setActive, isLoaded: signInLoaded } = useSignIn()
  const { signUp, isLoaded: signUpLoaded } = useSignUp()
  const clerk = useClerk()
  const router = useRouter()
  const { user, isLoaded } = useUser()

  // Sign in mutation
  const signInMutation = useMutation({
    mutationFn: async (email: string) => {
      if (!signInLoaded || !signIn) throw new Error('Authentication service not ready')

      await signIn.create({
        identifier: email,
        strategy: 'email_code',
      })
    },
    ...baseMutationConfig,
    onError: (error) => handleMutationError(error, 'Sign in'),
  })

  // Sign up mutation
  const signUpMutation = useMutation({
    mutationFn: async ({ email, password }: { email: string; password?: string }) => {
      if (!signUpLoaded || !signUp) throw new Error('Authentication service not ready')

      const signUpResult = await signUp.create({
        emailAddress: email,
        password: password,
      })

      await signUpResult.prepareEmailAddressVerification()
    },
    ...baseMutationConfig,
    onError: (error) => handleMutationError(error, 'Sign up'),
  })

  // Verify sign-in code mutation
  const verifySignInCodeMutation = useMutation({
    mutationFn: async (code: string) => {
      if (!signInLoaded || !signIn) throw new Error('Authentication service not ready')

      const result = await signIn.attemptFirstFactor({
        strategy: 'email_code',
        code,
      })

      if (result.status !== 'complete') throw new Error('Invalid verification code')

      await setActive?.({
        session: result.createdSessionId,
        navigate: () => {
          router.push('/')
        },
      })
      return result
    },
    ...baseMutationConfig,
    onError: (error) => handleMutationError(error, 'Verify sign-in code'),
  })

  // Verify sign-up code mutation
  const verifySignUpCodeMutation = useMutation({
    mutationFn: async (code: string) => {
      if (!signUpLoaded || !setActive) throw new Error('Authentication service not ready')

      const signUpAttempt = await signUp.attemptEmailAddressVerification({
        code,
      })

      if (signUpAttempt?.status !== 'complete') throw new Error('Invalid verification code')

      await setActive({
        session: signUpAttempt.createdSessionId,
        navigate: () => {
          router.push('/onboard')
        },
      })

      return signUpAttempt
    },
    ...baseMutationConfig,
    onError: (error) => handleMutationError(error, 'Verify sign-up code'),
  })

  // Resend sign-in code mutation
  const resendSignInCodeMutation = useMutation({
    mutationFn: async (email: string) => {
      if (!signInLoaded || !signIn) throw new Error('Authentication service not ready')

      const signInAttempt = await signIn.create({
        identifier: email,
        strategy: 'email_code',
      })

      if (signInAttempt?.status === 'complete') {
        await setActive({
          session: signInAttempt.createdSessionId,
          navigate: () => {
            router.push('/')
          },
        })
      }

      return signInAttempt
    },
    ...baseMutationConfig,
    onError: (error) => handleMutationError(error, 'Resend sign-in code'),
  })

  // Resend sign-up code mutation
  const resendSignUpCodeMutation = useMutation({
    mutationFn: async ({ email, password }: { email: string; password?: string }) => {
      if (!signUpLoaded || !signUp) throw new Error('Authentication service not ready')

      // Create a new sign-up instance for resend
      const signUpResult = await signUp.create({
        emailAddress: email,
        password,
      })

      await signUpResult.prepareEmailAddressVerification()
      return signUpResult
    },
    ...baseMutationConfig,
    onError: (error) => handleMutationError(error, 'Resend sign-up code'),
  })

  // Google auth mutation
  const googleAuthMutation = useMutation({
    mutationFn: async () => {
      if (!signIn) throw new Error('Authentication service not ready')

      await signIn.authenticateWithRedirect({
        strategy: 'oauth_google',
        redirectUrl: '/sso-callback',
        redirectUrlComplete: '/',
      })
    },
    ...baseMutationConfig,
    onError: (error) => handleMutationError(error, 'Google auth'),
  })

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: async () => {
      await clerk.signOut()
      router.push('/login')
    },
    ...baseMutationConfig,
    onError: (error) => handleMutationError(error, 'Logout'),
  })

  // DRY auth handlers using the helper
  const signInUser = createAuthHandler(signInMutation)
  const signUpUser = createAuthHandler(signUpMutation)
  const verifySignInCode = createAuthHandler(verifySignInCodeMutation)
  const verifySignUpCode = createAuthHandler(verifySignUpCodeMutation, { needsOnboarding: true })
  const resendSignInCode = createAuthHandler(resendSignInCodeMutation)
  const resendSignUpCode = createAuthHandler(resendSignUpCodeMutation)
  const handleGoogleAuth = createAuthHandler(googleAuthMutation)
  const logout = createAuthHandler(logoutMutation)

  return {
    // Clerk authentication state
    user,
    isLoaded,

    // Authentication actions
    signInUser,
    verifySignInCode,
    resendSignInCode,
    signUpUser,
    verifySignUpCode,
    resendSignUpCode,
    handleGoogleAuth,
    logout,
  }
}
